/**
 * The rows of the run rail, grouped by the day they ran.
 *
 * `RunRail` owns the chrome — width, scrolling, the filter — and this file owns a
 * row, the day grouping and the delete gate. Two levels because they change for
 * different reasons.
 *
 * ── The collapse is CSS, not state ──────────────────────────────────────────────
 *
 * Below `xl` (1280px) the rail is a ~56px strip of status dots and this component
 * renders the *same* markup with its text hidden. Deliberately not a JS media query:
 * that reads the viewport after the first paint, so the first frame is always the
 * wrong one, and it would put a layout decision in a hook that has to agree with a
 * Tailwind breakpoint in another file.
 */
import { Loader2, X } from 'lucide-react'
import { Button } from './ui/Button'
import { FailedState } from './ui/States'
import { StatusDot } from './ui/StatusDot'
import { cn, formatDuration, formatTimestamp } from '../lib/utils'
import type { RunSummary } from '../api/types'

/**
 * Statuses whose runs produced no ranking and so can be cleared.
 *
 * A UI hint only — the API decides, and it decides on whether the run has rows
 * in `leaderboard.targets` rather than on its status. That is deliberately
 * stricter: the run list payload carries no target count, so this set is the
 * best the sidebar can do, and offering the button on a run the server then
 * refuses is a 409 with an explanation, not a broken delete.
 */
const DELETABLE = new Set(['failed', 'cancelled', 'empty', 'blocked'])

/** What the API returns at most; `/leaderboard/runs` takes no offset. */
const RUN_LIMIT = 50

const MINUTE = 60_000
const DAY = 24 * 60 * MINUTE

/**
 * How long ago, coarsely, or null when there is no timestamp to be coarse about.
 *
 * Local to the rail rather than added to `lib/utils`, which is shared with three
 * other branches. The absolute stamp stays available in the row's `title`, and the
 * day header above it carries the date — so this only ever has to answer "how stale
 * is this", which is what a reader scanning fifty rows is actually asking.
 *
 * Null, not "—" and not "never": the caller decides what an absent start means, and
 * for a queued run it means something specific.
 */
function relativeTime(iso: string | null): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const minutes = Math.round((Date.now() - then) / MINUTE)
  // A negative delta is clock skew between this browser and the API, not the
  // future. "just now" is the least wrong thing to say about it.
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** Midnight, local, of the day an instant falls in. */
function startOfDay(ms: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function dayLabel(iso: string | null): string {
  if (!iso) return 'Undated'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'Undated'
  const days = Math.round((startOfDay(Date.now()) - startOfDay(then)) / DAY)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return new Date(then).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    // Only once it is genuinely another year. A year on every row of a
    // three-week-old history is noise that pushes the weekday off a 288px rail.
    year:
      new Date(then).getFullYear() === new Date().getFullYear()
        ? undefined
        : 'numeric',
  })
}

interface DayGroup {
  label: string
  runs: RunSummary[]
}

/**
 * Runs into day groups, **preserving the order they arrived in**.
 *
 * The API orders newest first and this must not second-guess it, so a run joins the
 * group for its label wherever that group already is, and a new label appends. A
 * sort here would be a second opinion about recency with nothing to say which one
 * was right.
 *
 * A queued run has no `started_at` — it has not started. It is grouped by
 * `updated_at`, which is when it was enqueued, because a run queued four minutes ago
 * belongs under "Today" and not in a bucket of its own. That is a grouping key, not
 * a displayed value: the row still says it has not started rather than borrowing the
 * enqueue time and calling it a start.
 */
function groupByDay(runs: RunSummary[]): DayGroup[] {
  const groups: DayGroup[] = []
  for (const run of runs) {
    const label = dayLabel(run.started_at ?? run.updated_at)
    const existing = groups.find((group) => group.label === label)
    if (existing) existing.runs.push(run)
    else groups.push({ label, runs: [run] })
  }
  return groups
}

interface Props {
  /** The runs to show — already filtered by the rail. */
  runs: RunSummary[]
  /**
   * How many runs there were before the filter. Loaded-and-empty and
   * filtered-to-nothing are two different states and get two different sentences.
   */
  totalRuns: number
  /**
   * Whether the run list is an answer yet. An empty `runs` is three different
   * things — still loading, loaded and genuinely empty, and failed to load — and
   * they need three different sentences. Without this the rail told a reader whose
   * fetch had just failed to start a sweep, which is advice on a measurement
   * nobody made.
   */
  runsLoaded: boolean
  /** Of the two not-loaded cases, the one that failed rather than is pending. */
  runsFailed: boolean
  selectedRunId: string | null
  onSelect: (runId: string) => void
  onDelete: (runId: string) => void
  /** Run id whose delete is in flight. */
  deleting: string | null
  /** Why the last delete was refused. Absent on success — the row disappears. */
  deleteError: string | null
}

export function RunList({
  runs,
  totalRuns,
  runsLoaded,
  runsFailed,
  selectedRunId,
  onSelect,
  onDelete,
  deleting,
  deleteError,
}: Props) {
  // Three states before "no runs" is even sayable. The order matters: `runs` is
  // empty in all three, and only the last one is a finding.
  if (runs.length === 0 && !runsLoaded) {
    return runsFailed ? (
      // Deliberately short, and deliberately not the API's sentence: that sentence
      // is already in the main panel with the Retry button, and at 288px it wraps
      // to six lines. This says which list is missing and stops.
      <p
        role="alert"
        className="hidden px-3 py-4 text-sm text-red-400 xl:block"
      >
        The run list did not load.
      </p>
    ) : (
      <p
        role="status"
        className="hidden px-3 py-4 text-sm text-slate-500 xl:block"
      >
        Loading runs…
      </p>
    )
  }

  if (runs.length === 0) {
    return (
      <p className="hidden px-3 py-4 text-sm text-slate-500 xl:block">
        {totalRuns === 0 ? (
          <>
            No runs recorded yet. Start one with{' '}
            <code className="font-mono text-xs text-slate-400">
              target-discovery run --autonomous
            </code>
            .
          </>
        ) : (
          <>No run matches that filter.</>
        )}
      </p>
    )
  }

  const groups = groupByDay(runs)

  return (
    <>
      {/* The API's own refusal, verbatim: a delete is declined for a reason — the
          run has ranked targets — and the reason is the answer, not a symptom. */}
      {deleteError && (
        <FailedState message={deleteError} className="mx-2 mb-2 hidden xl:flex" />
      )}

      {groups.map((group, groupIndex) => (
        <section key={group.label}>
          {/* Sticky within the rail's scroller. Hidden in the strip, where there is
              no room for a date — the rule below it is what survives, so the day
              boundaries are still visible. */}
          <h3
            className={cn(
              'sticky top-0 z-10 hidden bg-slate-950/95 px-3 py-1.5 font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500 backdrop-blur xl:block',
              groupIndex > 0 && 'mt-2 border-t border-slate-800/70 pt-3',
            )}
          >
            {group.label}
          </h3>
          {groupIndex > 0 && (
            <hr className="mx-3 my-2 border-slate-800 xl:hidden" />
          )}

          <ul className="space-y-0.5 px-1 xl:px-2">
            {group.runs.map((run) => {
              const selected = run.run_id === selectedRunId
              const when = relativeTime(run.started_at)
              return (
                // `group` so the delete affordance can stay hidden until the row is
                // hovered or focused: it is housekeeping, and a row of red crosses
                // reads as an error state.
                <li key={run.run_id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelect(run.run_id)}
                    aria-current={selected}
                    // The whole label, because in the collapsed strip the dot is the
                    // only visible thing and colour is not a name.
                    title={`${run.run_id} · ${formatTimestamp(run.started_at)}`}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
                      'justify-center xl:justify-start xl:px-3',
                      selected
                        ? 'bg-slate-800 text-slate-100'
                        : 'text-slate-300 hover:bg-slate-800/50',
                    )}
                  >
                    <StatusDot status={run.status} className="mt-1.5" />

                    <span className="hidden min-w-0 flex-1 xl:block">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate font-mono text-xs">
                          {run.run_id}
                        </span>
                        <span className="shrink-0 font-mono text-2xs tabular-nums text-slate-500">
                          {formatDuration(run.started_at, run.finished_at)}
                        </span>
                      </span>

                      <span className="mt-0.5 flex items-center gap-1.5 text-2xs text-slate-500">
                        {/* Absent, not zero: a queued run has not started, and
                            "0m ago" would be a claim that it just did. */}
                        <span>{when ?? 'Not started'}</span>
                        {/* Stage only matters while a run is live; on a finished run
                            it is just the last stage reached and reads as if still
                            in progress. */}
                        {run.status === 'running' && run.stage && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="truncate text-brand-300">
                              {run.stage}
                            </span>
                          </>
                        )}
                      </span>

                      {/* `salvaged` always carries a real `last_error` too — it is
                          the original `stream_error` the salvage recovered a
                          ranking behind, not a leftover from a stale stage. */}
                      {(run.status === 'failed' || run.status === 'salvaged') &&
                        run.last_error && (
                        <span
                          className="mt-1 block truncate pr-6 text-2xs text-red-400"
                          title={run.last_error}
                        >
                          {run.last_error}
                        </span>
                      )}
                    </span>
                  </button>

                  {/* A sibling of the row button, not a child: nested buttons are
                      invalid HTML and the inner click would also select the run.
                      Absolutely positioned so showing it does not reflow the row,
                      and strip-hidden because 56px has no room for two targets. */}
                  {DELETABLE.has(run.status) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(run.run_id)}
                      disabled={deleting === run.run_id}
                      aria-label={`Delete run ${run.run_id}`}
                      title="Remove this run from the history. It produced no ranking, so nothing scientific is lost; the checkpoint and any files it wrote stay."
                      className="absolute bottom-1 right-1 hidden h-6 w-6 px-0 text-slate-500 opacity-0 transition-opacity hover:bg-red-950/60 hover:text-red-300 focus-visible:opacity-100 group-hover:opacity-100 xl:inline-flex"
                    >
                      {deleting === run.run_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ))}

      {/* Only once the list is actually at the cap, and stated rather than turned
          into a "load more": `/leaderboard/runs` takes a limit and no offset, so
          there is no next page to fetch and a button would be a lie. */}
      {totalRuns >= RUN_LIMIT && (
        <p className="hidden px-3 pb-2 pt-4 text-2xs leading-relaxed text-slate-500 xl:block">
          The {RUN_LIMIT} most recent runs. Older runs are not listed.
        </p>
      )}
    </>
  )
}
